import _ from 'lodash'

import React from 'react'

import { DISPLAY_SHORT_DOT_DELAMINATE_DATE_FORMAT } from 'constants/date'
import { NEUTRAL_COLOURS } from 'constants/colors'
import { MF_SIGNATURE_TYPES } from 'services/medicationForms/constants'
import { BasicViewProps } from 'constants/models'
import { Administration, EmergencyDose } from 'services/administrations/models'

import { Avatar, DateString, DefinitionList, Grid, ModalBox, Spinner, TimeString, Typography } from 'components'

import i18n from 'translations'

import AdministrationParentSignatureModalForm, {
  AdministrationParentSignatureFormValues,
} from './components/AdministrationParentSignatureModalForm'
import { StyledBanner, StyledDivider } from './AdministrationParentSignatureModalStyled'

interface AdministrationParentSignatureModalViewProps extends BasicViewProps {
  administration?: Administration & EmergencyDose
  isSubmitting: boolean
  onCancelClick?: () => void
  onSubmit: (fields: AdministrationParentSignatureFormValues) => void
}

const AdministrationParentSignatureModalView: React.FC<AdministrationParentSignatureModalViewProps> = ({
  administration,
  isLoading,
  isSubmitting,
  onCancelClick,
  onSubmit,
}) => {
  const renderContent = () => {
    if (isLoading || !administration) {
      return <Spinner />
    }

    const { createdAt, medicationForm, signatures, takenAt, timeOfAdministration } = administration
    const { dose, medication } = administration?.medicationDetails || medicationForm?.medicationDetails || {}
    const { firstName, id: childId, photo, surname } = administration?.child || medicationForm?.child || {}

    const witness = _.find(signatures, { type: MF_SIGNATURE_TYPES.WITNESS })
    const administeredBy = _.find(signatures, { type: MF_SIGNATURE_TYPES.STAFF })

    const renderDefinitionItem = (label, value) => (
      <DefinitionList.Item
        label={(
          <Typography color={NEUTRAL_COLOURS.GRAY} bold>
            {label}
          </Typography>
        )}
        margin="0 20px 0 0"
        value={value}
      />
    )

    return (
      <React.Fragment>
        <Grid>
          <Grid.Item flex={{ desktop: 1 }} justifyContent={{ mobile: 'center' }}>
            <Avatar
              avatarSize="medium"
              initials={[firstName, surname]}
              src={photo}
              title={(
                <Typography fontSize={20} bold>
                  {firstName}
                  {' '}
                  {surname}
                </Typography>
              )}
            />
          </Grid.Item>
          <Grid.Item
            alignItems={{ desktop: 'center' }}
            flex={{ desktop: 1 }}
            justifyContent={{ desktop: 'flex-end', mobile: 'center' }}
          >
            <div>
              <Typography color={NEUTRAL_COLOURS.GRAY} fontSize={16} margin="0 0 4px 0">
                {i18n.t('global:Date')}
              </Typography>
              <Typography fontSize={18} bold>
                <DateString
                  date={(takenAt || createdAt) as Date}
                  format={DISPLAY_SHORT_DOT_DELAMINATE_DATE_FORMAT}
                />
              </Typography>
            </div>
          </Grid.Item>
        </Grid>
        <StyledBanner>
          {medication}
        </StyledBanner>
        <DefinitionList>
          {renderDefinitionItem(
            i18n.t('modals:Administration:AddAdministration:Form:Dose:label'),
            <Typography fontSize={18} bold primary>{dose}</Typography>,
          )}
          {renderDefinitionItem(
            i18n.t('global:Time'),
            <Typography fontSize={18} bold>
              {timeOfAdministration
                ? <TimeString date={timeOfAdministration} local={false} />
                : <TimeString date={takenAt as Date} />}
            </Typography>,
          )}
          {renderDefinitionItem(
            i18n.t('modals:Administration:AddAdministration:Form:Signatures:StaffDropdown:label'),
            <Avatar
              avatarSize="small"
              initials={administeredBy.owner.name.split(' ')}
              src={administeredBy.owner.photo}
              title={administeredBy.owner.name}
            />,
          )}
          {renderDefinitionItem(
            i18n.t('modals:Administration:AddAdministration:Form:Signatures:WitnessDropdown:label'),
            <Avatar
              avatarSize="small"
              initials={witness.owner.name.split(' ')}
              src={witness.owner.photo}
              title={witness.owner.name}
            />,
          )}
        </DefinitionList>
        <StyledDivider />
        <AdministrationParentSignatureModalForm
          childId={childId}
          isSubmitting={isSubmitting}
          onCancelClick={onCancelClick}
          onSubmit={onSubmit}
        />
      </React.Fragment>
    )
  }

  return (
    <ModalBox
      maxWidth={660}
      title={i18n.t('modals:Administration:ParentSignature:title')}
      iosPolyfill
      onCloseClick={onCancelClick}
    >
      {renderContent()}
    </ModalBox>
  )
}

export default AdministrationParentSignatureModalView
